import React, { useState, useEffect, createContext, useContext } from 'react';
import { auth, googleProvider, db } from '../firebase/config';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';


const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState(null); // 'manager' | 'artist' | 'crew' | 'unauthorized'

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // FALLBACK: Auto-allow the primary owner to prevent complete lockout
                if (currentUser.email === 'directorprogrammesdis@gmail.com' || currentUser.email === 'inapsproductionunit@gmail.com') {
                    setUser(currentUser);
                    setRole('manager');
                    setLoading(false);
                    return;
                }

                try {
                    const roleDocRef = doc(db, 'system_roles', currentUser.email);
                    const roleDocSnap = await getDoc(roleDocRef);

                    if (roleDocSnap.exists()) {
                        const data = roleDocSnap.data();
                        // Assume inactive status means unauthorized
                        setRole(data.status === 'archived' ? 'unauthorized' : data.role);
                    } else {
                        setRole('unauthorized'); // Strict gating
                    }
                } catch (error) {
                    console.error("Auth routing error:", error);
                    setRole('unauthorized');
                }
                
                setUser(currentUser);
            } else {
                setUser(null);
                setRole(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const login = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            console.error("Login failed:", error);
        }
    };

    const logout = () => signOut(auth);

    return (
        <AuthContext.Provider value={{ user, loading, role, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

/**
 * AuthGuard Component
 * Enforces domain-aware security and whitelisting.
 */
export const AuthGuard = ({ children }) => {
    const { user, loading, role, login } = useAuth();
    const hostname = window.location.hostname;
    const isArtistPortal = hostname === 'moodprint.web.app';
    const isManagementPortal = hostname === 'moodcmsspace.web.app';

    if (loading) return (
        <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-white/10 border-t-white rounded-full animate-spin" />
        </div>
    );

    if (!user) {
        return <Navigate to="/" replace />;
    }

    // SECURITY LOCKDOWN:
    // Unauthorized accounts blocked entirely
    if (role === 'unauthorized') {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center p-10 text-center">
                <h1 className="text-4xl font-black text-red-500 uppercase tracking-tighter mb-4">UNAUTHORIZED</h1>
                <p className="text-zinc-500 max-w-sm font-bold uppercase tracking-widest text-xs">
                    Your email is not on the System Whitelist. Access to the DIS Nexus is strictly curated.
                </p>
                <button 
                    onClick={() => signOut(auth)}
                    className="mt-8 px-8 py-3 bg-white text-black font-black uppercase tracking-widest text-xs rounded-full"
                >
                    Switch Account
                </button>
            </div>
        );
    }

    // If on Management Portal, ONLY allow Managers.
    if (isManagementPortal && role !== 'manager' && role !== 'ecosystem_director') {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center p-10 text-center">
                <h1 className="text-4xl font-black text-red-500 uppercase tracking-tighter mb-4">ACCESS DENIED</h1>
                <p className="text-zinc-500 max-w-sm font-bold uppercase tracking-widest text-xs">
                    You are attempting to access the Nexus Curation Layer without Management credentials.
                </p>
                <button 
                    onClick={() => signOut(auth)}
                    className="mt-8 px-8 py-3 bg-white text-black font-black uppercase tracking-widest text-xs rounded-full"
                >
                    Switch Account
                </button>
            </div>
        );
    }

    return children;
};

export const RoleGuard = ({ allowedRoles, children }) => {
    const { role, loading } = useAuth();
    
    if (loading) return null;
    
    // Super admins always get access
    if (role === 'manager' || role === 'ecosystem_director' || role === 'admin') {
        return children;
    }
    
    if (allowedRoles && !allowedRoles.includes(role)) {
        return (
            <div className="flex h-[60vh] items-center justify-center flex-col gap-4">
                <h1 className="text-3xl font-black text-[#8B1A2D] uppercase tracking-tighter">Clearance Required</h1>
                <p className="text-zinc-500 font-bold uppercase tracking-widest text-xs">
                    Your assigned role ({role}) does not have clearance for this operational hub.
                </p>
            </div>
        );
    }

    return children;
};
